import { type ReactNode, useCallback, useEffect, useState } from 'react'
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom'

import { CrashBoundary } from './components/CrashBoundary'
import { type Catalogue, LOCALES, type LocaleCode, loadCatalogue } from './lib/i18n'
import { I18nProvider, rememberLocale, useT } from './lib/i18n/I18nProvider'
import { messageForError } from './lib/i18n/errors'
import { landingPath } from './lib/i18n/paths'
import { Dashboard } from './views/Dashboard'
import { Editor } from './views/Editor'
import { LandingPage } from './views/Landing'

/** Where IndexedDB stands. Only the plan routes wait for it. */
export type StorageState = { status: 'opening' } | { status: 'ready' } | { status: 'failed'; error: unknown }

interface Props {
  /** The language the visitor starts in, and its catalogue — loaded by main.tsx before this renders. */
  locale: LocaleCode
  catalogue: Catalogue
  /** `api.ready()`, started by main.tsx: the landing page never waits for it. */
  storage: Promise<void>
}

export default function App({ locale: initialLocale, catalogue: initialCatalogue, storage: opening }: Props) {
  const [catalogue, setCatalogue] = useState(initialCatalogue)
  const [locale, setLocale] = useState(initialLocale)
  const [storage, setStorage] = useState<StorageState>({ status: 'opening' })

  useEffect(() => {
    let live = true
    opening.then(
      () => live && setStorage({ status: 'ready' }),
      (error: unknown) => live && setStorage({ status: 'failed', error }),
    )
    return () => {
      live = false
    }
  }, [opening])

  /** Shows a language without choosing it: a language page's URL, or a plan's own language. */
  const showLocale = useCallback(async (next: LocaleCode) => {
    const strings = await loadCatalogue(next)
    setCatalogue(strings)
    setLocale(next)
  }, [])

  /**
   * Switching language outside a plan. Remembered in this browser, so the next
   * plan created inherits it — a plan carries its own language once it exists.
   */
  const changeLocale = useCallback(
    async (next: LocaleCode) => {
      await showLocale(next)
      rememberLocale(next)
    },
    [showLocale],
  )

  return (
    <I18nProvider locale={locale} catalogue={catalogue} onLocaleChange={changeLocale}>
      <CrashBoundary>
        <Router>
          <AppRoutes storage={storage} onLocaleChange={changeLocale} onLocaleShow={showLocale} />
        </Router>
      </CrashBoundary>
    </I18nProvider>
  )
}

interface RoutesProps {
  storage: StorageState
  onLocaleChange: (locale: LocaleCode) => void
  onLocaleShow: (locale: LocaleCode) => void
}

/**
 * The landing page at `/` and at each language's own path (lib/i18n/paths.ts),
 * whatever storage does: an in-app browser or a private window that refuses
 * IndexedDB still gets the page a shared link promised, and "Start planning"
 * takes it to /plans, which says why nothing can be saved.
 */
export function AppRoutes({ storage, onLocaleChange, onLocaleShow }: RoutesProps) {
  return (
    <Routes>
      {LOCALES.map((code) => (
        <Route
          key={code}
          path={landingPath(code)}
          element={<LandingPage lang={code === 'en' ? null : code} onLocaleShow={onLocaleShow} />}
        />
      ))}
      <Route path="/plans" element={<NeedsStorage storage={storage}>{<Dashboard />}</NeedsStorage>} />
      <Route
        path="/plan/:planId"
        element={<NeedsStorage storage={storage}>{<Editor onLocaleChange={onLocaleChange} />}</NeedsStorage>}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

/**
 * The plan routes need IndexedDB. A browser can refuse it (some private
 * windows do), and it is better to say so on arrival than to let someone build
 * a room that cannot be saved.
 */
function NeedsStorage({ storage, children }: { storage: StorageState; children: ReactNode }) {
  const t = useT()
  if (storage.status === 'opening') {
    return (
      <div className="boot">
        <div className="boot-mark" />
      </div>
    )
  }
  if (storage.status === 'failed') {
    return (
      <div className="boot boot--error">
        <h1>{t.errors.bootTitle}</h1>
        <p>{messageForError(t, storage.error)}</p>
      </div>
    )
  }
  return <>{children}</>
}
